import { z } from "zod"

import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"
import {
  notebookRecurrenceSchema,
  type NotebookRecurrence,
} from "@/utils/notebook-recurrence"

export const DEFAULT_RECURRENCE: NotebookRecurrence = {
  cadence: "daily",
  interval: 1,
  time: "08:00",
  timezone: "America/Los_Angeles",
}

const scheduleSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  updated_at: z.string(),
  recurrence: notebookRecurrenceSchema,
  instructions: z.string(),
  next_run_at: z.string().nullable(),
  last_run: z
    .object({
      status: z.string(),
      notebook_id: z.string().optional(),
      error: z.string().optional(),
    })
    .nullable(),
})
export type GenerativeSchedule = z.infer<typeof scheduleSchema>

export async function generativeScheduleRequest(
  scope: {
    organizationSlug: string
    workspaceId: string
    notebookId: string
  },
  update?: {
    recurrence: NotebookRecurrence
    instructions: string
    enabled: boolean
    expected_updated_at: string | null
    expected_notebook_updated_at: string
  },
  signal?: AbortSignal,
): Promise<{
  schedule: GenerativeSchedule | null
  scheduling_available: boolean
}> {
  const headers = await getOrganizationBearerHeaders(scope.organizationSlug)
  const query = new URLSearchParams({
    organization: scope.organizationSlug,
    workspace: scope.workspaceId,
  })
  const response = await fetch(
    `/api/sofia/notebooks/${encodeURIComponent(scope.notebookId)}/schedule?${query}`,
    {
      method: update ? "PUT" : "GET",
      headers: { ...headers, "Content-Type": "application/json" },
      ...(update
        ? {
            body: JSON.stringify({
              organization: scope.organizationSlug,
              workspace: scope.workspaceId,
              ...update,
            }),
          }
        : {}),
      signal,
      cache: "no-store",
    },
  )
  const data: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const error = z.object({ error: z.string() }).safeParse(data)
    throw new Error(
      error.success ? error.data.error : "Could not load notebook scheduling.",
    )
  }
  return z
    .object({
      schedule: scheduleSchema.nullable(),
      scheduling_available: z.boolean(),
    })
    .parse(data)
}
